import { useDraggable, useDroppable } from '@dnd-kit/core';
import { styled } from '@mui/material';
import Box from '@mui/material/Box';
import { memo } from 'react';

import { ELayoutManagerMode, SwapIcon } from '#shared';

import { blockSelectors, useSelectWidgetInstanceById } from '../../slices';
import {
  useSelectWidgetManagerDraggingWidget,
  type WidgetId,
  widgetRegistry,
} from '../../widgetManager';
import { useLayoutMode } from '../model';

import { BlockPlaceholder } from './BlockPlaceholder';

const PREFIX = 'TSMED-LayoutManager-Block';

export const blockClasses = {
  root: `${PREFIX}-root`,
  label: `${PREFIX}-label`,
  body: `${PREFIX}-body`,
  swapOverlay: `${PREFIX}-swapOverlay`,
} as const;

const Root = styled(Box, {
  shouldForwardProp: prop =>
    !['styleSlots', '$isSwapTarget'].includes(prop as string),
})<{ styleSlots?: BlockProps['styleSlots']; $isSwapTarget?: boolean }>(
  ({ styleSlots, theme }) => ({
    [`&.${blockClasses.root}`]: {
      width: '100%',
      height: '100%',
      minWidth: 0,
      minHeight: 0,
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      ...styleSlots?.block?.root,
    },
    [`.${blockClasses.body}`]: {
      width: '100%',
      height: '100%',
      minWidth: 0,
      minHeight: 0,
      overflow: 'hidden',
      display: 'flex',
      ...styleSlots?.block?.body,
    },
    [`.${blockClasses.swapOverlay}`]: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: `${theme.palette.mapBlue[300]}50`,
      backdropFilter: 'blur(2px)',
      border: `3px solid ${theme.palette.mapBlue[300]}`,
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 60,
      pointerEvents: 'none',
    },
  }),
);

export interface BlockProps {
  blockId: WidgetId;
  mode: ELayoutManagerMode;
  styleSlots?: {
    block?: Partial<Record<keyof typeof blockClasses, React.CSSProperties>>;
  };
}

export const Block = memo(function Block({
  blockId,
  mode,
  styleSlots,
}: BlockProps) {
  const editingContext = useLayoutMode();
  const block = blockSelectors.useSelectBlockById(blockId);
  const instance = useSelectWidgetInstanceById(block?.instanceId ?? blockId);
  const draggingWidget = useSelectWidgetManagerDraggingWidget();

  // Get additional instances if defined (must call hooks at top level, fixed count)
  // We support max 2 additional instances (left and right)
  const additionalInstances = block?.additionalInstances || [];
  const leftInstance = useSelectWidgetInstanceById(
    (additionalInstances.find(ai => ai.position === 'left')?.instanceId ??
      'placeholder-none') as WidgetId,
  );
  const rightInstance = useSelectWidgetInstanceById(
    (additionalInstances.find(ai => ai.position === 'right')?.instanceId ??
      'placeholder-none') as WidgetId,
  );

  // Only use editing context values in EDIT mode
  const selectedWidgetId =
    mode === ELayoutManagerMode.EDIT ? editingContext.selectedWidgetId : null;

  const dragEnabled = mode === ELayoutManagerMode.EDIT;

  // Draggable for picking up this block
  const {
    attributes,
    listeners,
    setNodeRef: setDragRef,
    isDragging,
  } = useDraggable({
    id: blockId,
    disabled: !dragEnabled,
  });

  // Droppable for receiving other blocks (swap target)
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: blockId,
    disabled: !dragEnabled,
  });

  // Combine refs for both drag and drop on same element
  const setNodeRef = (node: HTMLElement | null) => {
    setDragRef(node);
    setDropRef(node);
  };

  // Show swap indicator when something is dragged over this block (not itself)
  const isSwapTarget = isOver && !isDragging && draggingWidget !== null;

  if (!block || !instance) {
    return null;
  }

  const InstanceComponent = widgetRegistry.get(instance.type);

  if (!InstanceComponent) {
    return null;
  }

  const hasAdditionalInstances = additionalInstances.length > 0;
  const leftConfig = additionalInstances.find(ai => ai.position === 'left');
  const rightConfig = additionalInstances.find(ai => ai.position === 'right');
  const direction = block.additionalInstancesDirection || 'row'; // Default to row

  return (
    <Root
      id={blockId}
      className={blockClasses.root}
      ref={dragEnabled ? setNodeRef : undefined}
      sx={{
        zIndex: isDragging ? 1 : selectedWidgetId === blockId ? 100 : 'auto',
        flexDirection: hasAdditionalInstances ? direction : 'column',
        opacity: isDragging ? 0.4 : 1,
        transition: 'opacity 150ms ease',
        touchAction: dragEnabled ? 'none' : undefined,
        userSelect: dragEnabled ? 'none' : undefined,
      }}
      {...(dragEnabled ? listeners : {})}
      {...(dragEnabled ? attributes : {})}
      styleSlots={styleSlots}
      $isSwapTarget={isSwapTarget}
    >
      {isSwapTarget && (
        <Box className={blockClasses.swapOverlay}>
          <SwapIcon />
        </Box>
      )}
      <Box
        className={blockClasses.body}
        sx={{
          display: 'flex',
          flexDirection: hasAdditionalInstances ? direction : 'column',
          gap: 0,
          width: '100%',
          height: '100%',
        }}
      >
        {mode === ELayoutManagerMode.RENDER ? (
          // RENDER mode: Show actual widgets
          hasAdditionalInstances ? (
            <>
              {/* Left additional instance */}
              {leftInstance && leftConfig && (
                <Box
                  sx={{
                    flex: '0 0 150px',
                    minWidth: 0,
                    minHeight: 0,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {(() => {
                    const LeftComponent = widgetRegistry.get(leftInstance.type);
                    return LeftComponent ? (
                      <LeftComponent
                        instance={{
                          ...leftInstance,
                          ...(leftConfig.instanceIndex !== undefined
                            ? { instanceIndex: leftConfig.instanceIndex }
                            : {}),
                        }}
                      />
                    ) : null;
                  })()}
                </Box>
              )}

              {/* Primary instance */}
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  minHeight: 0,
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <InstanceComponent
                  instance={{
                    ...instance,
                    ...(block.instanceIndex !== undefined
                      ? { instanceIndex: block.instanceIndex }
                      : {}),
                  }}
                />
              </Box>

              {/* Right additional instance */}
              {rightInstance && rightConfig && (
                <Box
                  sx={{
                    flex: '0 0 150px',
                    minWidth: 0,
                    minHeight: 0,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {(() => {
                    const RightComponent = widgetRegistry.get(
                      rightInstance.type,
                    );
                    return RightComponent ? (
                      <RightComponent
                        instance={{
                          ...rightInstance,
                          ...(rightConfig.instanceIndex !== undefined
                            ? { instanceIndex: rightConfig.instanceIndex }
                            : {}),
                        }}
                      />
                    ) : null;
                  })()}
                </Box>
              )}
            </>
          ) : (
            <InstanceComponent
              instance={{
                ...instance,
                // Pass instanceIndex from block to widget for multi-instance support
                ...(block.instanceIndex !== undefined
                  ? { instanceIndex: block.instanceIndex }
                  : {}),
              }}
            />
          )
        ) : // EDIT mode: Show placeholder(s)
        hasAdditionalInstances ? (
          <>
            {/* Left additional instance placeholder */}
            {leftInstance && leftConfig && (
              <Box
                sx={{
                  flex: '0 0 150px',
                  minWidth: 0,
                  minHeight: 0,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <BlockPlaceholder
                  widget={{
                    ...leftInstance,
                    ...(leftConfig.instanceIndex !== undefined
                      ? { instanceIndex: leftConfig.instanceIndex }
                      : {}),
                  }}
                  isSelected={selectedWidgetId === blockId}
                  isDragging={isDragging}
                  isOver={isOver}
                />
              </Box>
            )}

            {/* Primary instance placeholder */}
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                minHeight: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <BlockPlaceholder
                widget={instance}
                isSelected={selectedWidgetId === blockId}
                isDragging={isDragging}
                isOver={isOver}
              />
            </Box>

            {/* Right additional instance placeholder */}
            {rightInstance && rightConfig && (
              <Box
                sx={{
                  flex: '0 0 150px',
                  minWidth: 0,
                  minHeight: 0,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <BlockPlaceholder
                  widget={{
                    ...rightInstance,
                    ...(rightConfig.instanceIndex !== undefined
                      ? { instanceIndex: rightConfig.instanceIndex }
                      : {}),
                  }}
                  isSelected={selectedWidgetId === blockId}
                  isDragging={isDragging}
                  isOver={isOver}
                />
              </Box>
            )}
          </>
        ) : (
          <BlockPlaceholder
            widget={instance}
            isSelected={selectedWidgetId === blockId}
            isDragging={isDragging}
            isOver={isOver}
          />
        )}
      </Box>
    </Root>
  );
});
